import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Popover } from './Popover';

/**
 * A button that opens a small menu under itself.
 *
 * Three screens wanted the same thing within a week -- the board's kebab, the
 * order list's sort and grouping, the map's drawing options -- and the first
 * two were already diverging in the details that matter: whether a click
 * outside closes it, whether Escape does, whether picking something closes it.
 * One implementation, so those answers are the same wherever a menu appears.
 *
 * A menu, not a modal: the scrim behind it is transparent and catches the click
 * that dismisses it, so the board or the list stays readable underneath.
 */

const MenuContext = createContext<{ close: () => void }>({ close: () => {} });

export function Menu({
  label,
  icon,
  align = 'left',
  className,
  children,
}: {
  /** What the trigger says to a screen reader. */
  label: string;
  icon: ReactNode;
  align?: 'left' | 'right';
  /** Extra classes on the trigger, where a screen sizes it to its own chrome. */
  className?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  /*
   * Escape closes it and puts focus back on the trigger. Without the second
   * half, dismissing a menu from the keyboard drops focus onto the document and
   * the next Tab starts again from the top of the page.
   */
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      anchorRef.current?.querySelector('button')?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div className="pop-anchor menu" ref={anchorRef}>
      <button
        type="button"
        className={`menu-trigger${className ? ` ${className}` : ''}`}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {icon}
      </button>
      {/* Floats above the page, so a menu in a scrolling table or drawer is never cropped. */}
      <Popover open={open} onClose={() => setOpen(false)} align={align} className="menu-pop" role="menu">
        <MenuContext.Provider value={{ close: () => setOpen(false) }}>{children}</MenuContext.Provider>
      </Popover>
    </div>
  );
}

/** A heading over one group of items. */
export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className="menu-label">{children}</div>;
}

/**
 * A switch. Leaves the menu open, because these come in sets and nobody wants
 * to reopen the menu to turn on the second one.
 */
export function MenuToggle({
  label,
  hint,
  checked,
  disabled,
  title,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  disabled?: boolean;
  title?: string;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      className="pop-item"
      role="menuitemcheckbox"
      aria-checked={checked}
      disabled={disabled}
      title={title}
      onClick={() => onChange(!checked)}
    >
      <span className="pop-item-main">
        {label}
        {hint ? <small>{hint}</small> : null}
      </span>
      <span className="menu-switch" data-on={checked ? 'true' : undefined} />
    </button>
  );
}

/**
 * One of several. Closes the menu, because choosing is the whole errand and
 * leaving it open hides the list the choice just reordered.
 */
export function MenuChoice({
  label,
  hint,
  checked,
  onSelect,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onSelect: () => void;
}) {
  const { close } = useContext(MenuContext);
  return (
    <button
      type="button"
      className="pop-item"
      role="menuitemradio"
      aria-checked={checked}
      aria-selected={checked}
      onClick={() => {
        onSelect();
        close();
      }}
    >
      <span className="pop-item-main">
        {label}
        {hint ? <small>{hint}</small> : null}
      </span>
      {checked ? <TickIcon /> : null}
    </button>
  );
}

/** An action. Closes the menu, then runs. */
export function MenuItem({
  label,
  hint,
  danger,
  disabled,
  onSelect,
}: {
  label: string;
  hint?: string;
  danger?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}) {
  const { close } = useContext(MenuContext);
  return (
    <button
      type="button"
      className="pop-item"
      role="menuitem"
      data-tone={danger ? 'danger' : undefined}
      disabled={disabled}
      onClick={() => {
        close();
        onSelect();
      }}
    >
      <span className="pop-item-main">
        {label}
        {hint ? <small>{hint}</small> : null}
      </span>
    </button>
  );
}

function TickIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" aria-hidden="true" className="menu-tick">
      <path
        d="M2.5 7.5L5.5 10.5L11.5 3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Three dots: the menu that belongs to the thing it sits beside. */
export function KebabIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <circle cx="7" cy="3" r="1.3" fill="currentColor" />
      <circle cx="7" cy="7" r="1.3" fill="currentColor" />
      <circle cx="7" cy="11" r="1.3" fill="currentColor" />
    </svg>
  );
}

/** Layers: how a map is drawn, rather than what is on it. */
export function LayersIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M8 1.6l6 3-6 3-6-3 6-3z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <path
        d="M2.4 8L8 10.8 13.6 8M2.4 11.2L8 14l5.6-2.8"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
